import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import {
  AggregateDomain,
  AggregateDomainReconciliationRun,
  AggregateRollupIntegrityIssue,
  AggregateRollupIntegrityReport,
  ApiError,
  api
} from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type ComparisonRow = {
  domainId: string;
  periodId: string;
  orgUnitId: string;
  trackerValue: number;
  submittedValue: number;
  variance: number;
};

export default function AdminReconciliation() {
  const [latest, setLatest] = useState<AggregateDomainReconciliationRun | null>(null);
  const [comparisons, setComparisons] = useState<ComparisonRow[]>([]);
  const [domains, setDomains] = useState<AggregateDomain[]>([]);
  const [domainReports, setDomainReports] = useState<Record<string, AggregateRollupIntegrityReport>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [openReport, setOpenReport] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [recon, doms] = await Promise.all([
        api.aggregateReconciliation(),
        api.aggregateDomains()
      ]);
      setLatest(recon.latest);
      setComparisons(recon.comparisons ?? []);
      setDomains(doms.domains ?? []);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const onRunReconciliation = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.runAggregateReconciliation();
      setBanner(`Reconciliation completed (${r.status}). Source variances: ${r.sourceFactVarianceCount}, tracker variances: ${r.trackerVarianceCount}, metadata issues: ${r.metadataIssueCount}.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRunDhis2Reconciliation = async (scope: "aggregate" | "tracker") => {
    setBusy(true);
    setErr(null);
    try {
      const job = await api.runDhis2Reconciliation({ scope });
      setBanner(`DHIS2 ${scope} reconciliation enqueued (job ${job.id}, status ${job.status}).`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCheckIntegrity = async (domainId: string) => {
    try {
      const r = await api.validateAggregateRollupIntegrity(domainId);
      setDomainReports({ ...domainReports, [domainId]: r.report });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onCheckAll = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.validateAggregateRollupIntegrity();
      // Group results back per domain so per-row drawers work.
      const grouped: Record<string, AggregateRollupIntegrityReport> = {};
      for (const dId of r.report.domainIds) {
        const subset = r.report.results.filter((row) => row.domainId === dId);
        grouped[dId] = {
          ...r.report,
          domainIds: [dId],
          totalRollupFacts: subset.filter((s) => s.status !== "orphaned-lineage").length,
          totalLineageRows: subset.reduce((sum, s) => sum + s.lineageRowCount, 0),
          matchCount: subset.filter((s) => s.status === "match").length,
          mismatchCount: subset.filter((s) => s.status === "mismatch").length,
          orphanedLineageCount: subset.filter((s) => s.status === "orphaned-lineage").length,
          results: subset
        };
      }
      setDomainReports(grouped);
      setBanner(`Integrity check complete: ${r.report.matchCount} match, ${r.report.mismatchCount} mismatch, ${r.report.orphanedLineageCount} orphaned across ${r.report.domainIds.length} domains.`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRestate = async (domainId: string, mode: "incremental" | "full-rebuild") => {
    if (mode === "full-rebuild" && !confirm(`Full-rebuild rollups for ${domainId}? This drops and recomputes every rollup fact.`)) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.restateAggregateDomain(domainId, mode);
      setBanner(`Restate (${mode}) for ${domainId}: ${r.run.producedRollupCount} rollups, ${r.run.affectedFactCount} facts.`);
      // Refresh integrity for this domain
      const integ = await api.validateAggregateRollupIntegrity(domainId);
      setDomainReports({ ...domainReports, [domainId]: integ.report });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const totals = useMemo(() => {
    let match = 0, mismatch = 0, orphan = 0, checked = 0;
    for (const dId of Object.keys(domainReports)) {
      const r = domainReports[dId];
      match += r.matchCount;
      mismatch += r.mismatchCount;
      orphan += r.orphanedLineageCount;
      checked += 1;
    }
    return { match, mismatch, orphan, checked };
  }, [domainReports]);

  const openReportDomain = openReport ? domainReports[openReport] : null;

  const integrityRowsSource = useMemo(
    () => (openReportDomain ? openReportDomain.results.filter((r) => r.status !== "match") : []),
    [openReportDomain]
  );

  const t1 = useTableState(comparisons as unknown as Array<Record<string, unknown>>, {
    searchFields: ["domainId", "periodId", "orgUnitId", "trackerValue", "submittedValue", "variance"],
    initialPageSize: 25
  });
  const t2 = useTableState(integrityRowsSource as unknown as Array<Record<string, unknown>>, {
    searchFields: ["status", "rollupFactId", "rollupValue", "lineageSum", "difference", "lineageRowCount"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Reconciliation</h1>
          <p>Validate aggregate rollups against their source lineage, and reconcile platform totals against the upstream DHIS2 source.</p>
        </div>
        <div className="actions">
          <button className="btn" type="button" onClick={() => void refresh()} disabled={loading || busy}>Refresh</button>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      <section className="card" style={{ marginBottom: 16 }}>
        <h2>Source-vs-platform reconciliation (DHIS2)</h2>
        <p className="muted">Compares submitted aggregate facts and tracker events in the platform against what currently exists in the upstream DHIS2 source.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" type="button" disabled={busy} onClick={() => void onRunReconciliation()}>Run aggregate reconciliation (in-process)</button>
          <button className="btn" type="button" disabled={busy} onClick={() => void onRunDhis2Reconciliation("aggregate")}>Enqueue DHIS2 aggregate reconciliation</button>
          <button className="btn" type="button" disabled={busy} onClick={() => void onRunDhis2Reconciliation("tracker")}>Enqueue DHIS2 tracker reconciliation</button>
        </div>
        {latest ? (
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="pill pill-info">Last run: {new Date(latest.startedAt).toLocaleString()}</span>
            <span className={`pill ${latest.status === "completed" ? "pill-ok" : latest.status === "failed" ? "pill-warn" : "pill-info"}`}>
              {latest.status}
            </span>
            <span className={`pill ${latest.sourceFactVarianceCount === 0 ? "pill-muted" : "pill-warn"}`}>
              Source variances: {latest.sourceFactVarianceCount}
            </span>
            <span className={`pill ${latest.trackerVarianceCount === 0 ? "pill-muted" : "pill-warn"}`}>
              Tracker variances: {latest.trackerVarianceCount}
            </span>
            <span className={`pill ${latest.metadataIssueCount === 0 ? "pill-muted" : "pill-warn"}`}>
              Metadata issues: {latest.metadataIssueCount}
            </span>
          </div>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>No reconciliation run yet.</p>
        )}
      </section>

      <section className="card" style={{ marginBottom: 16 }}>
        <h2>Aggregate-vs-rollup integrity</h2>
        <p className="muted">Per domain, verifies that each rollup fact equals the sum of its lineage source values, and surfaces orphaned lineage rows.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <button className="btn" type="button" disabled={busy || domains.length === 0} onClick={() => void onCheckAll()}>Check all domains</button>
          {totals.checked > 0 && (
            <>
              <span className="pill pill-info">Checked domains: {totals.checked}</span>
              <span className="pill pill-ok">Match: {totals.match}</span>
              <span className={`pill ${totals.mismatch === 0 ? "pill-muted" : "pill-warn"}`}>Mismatch: {totals.mismatch}</span>
              <span className={`pill ${totals.orphan === 0 ? "pill-muted" : "pill-warn"}`}>Orphaned: {totals.orphan}</span>
            </>
          )}
        </div>
        {loading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : domains.length === 0 ? (
          <EmptyState icon="🧩" title="No aggregate domains" description="Create an aggregate domain in the Studio first." />
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Match</th>
                  <th>Mismatch</th>
                  <th>Orphaned</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => {
                  const r = domainReports[d.id];
                  return (
                    <tr key={d.id}>
                      <td>
                        <div><strong>{d.name}</strong></div>
                        <div className="soft"><code>{d.id}</code></div>
                      </td>
                      <td>
                        {d.archived ? <span className="pill pill-muted">Archived</span> :
                         d.active ? <span className="pill pill-ok">Active</span> :
                         <span className="pill pill-info">Draft</span>}
                      </td>
                      <td>{r ? r.matchCount : <span className="soft">—</span>}</td>
                      <td>{r ? <span className={r.mismatchCount === 0 ? "" : "alert-warn"}>{r.mismatchCount}</span> : <span className="soft">—</span>}</td>
                      <td>{r ? r.orphanedLineageCount : <span className="soft">—</span>}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void onCheckIntegrity(d.id)}>Check</button>
                          {r && <button type="button" className="btn btn-sm" onClick={() => setOpenReport(d.id)}>Report</button>}
                          <button type="button" className="btn btn-sm" disabled={busy || d.archived} onClick={() => void onRestate(d.id, "incremental")}>Restate (inc.)</button>
                          <button type="button" className="btn btn-sm" disabled={busy || d.archived} onClick={() => void onRestate(d.id, "full-rebuild")}>Restate (full)</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Tracker-vs-submitted variance</h2>
        <p className="muted">For domains that source from both tracker and submitted aggregate, this lists per-period/org-unit variances between the two source classes.</p>
        {comparisons.length === 0 ? (
          <EmptyState icon="✅" title="No variances detected" description="Either no hybrid domains exist, or all tracker and submitted-aggregate totals agree." />
        ) : (
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={t1.search}
              setSearch={t1.setSearch}
              page={t1.page}
              setPage={t1.setPage}
              pageSize={t1.pageSize}
              setPageSize={t1.setPageSize}
              totalPages={t1.totalPages}
              totalRows={t1.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Period</th>
                  <th>Org unit</th>
                  <th style={{ textAlign: "right" }}>Tracker</th>
                  <th style={{ textAlign: "right" }}>Submitted</th>
                  <th style={{ textAlign: "right" }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {(t1.paged as unknown as ComparisonRow[]).map((c, i) => (
                  <tr key={`${c.domainId}-${c.periodId}-${c.orgUnitId}-${i}`}>
                    <td><code>{c.domainId}</code></td>
                    <td>{c.periodId}</td>
                    <td>{c.orgUnitId}</td>
                    <td style={{ textAlign: "right" }}>{c.trackerValue}</td>
                    <td style={{ textAlign: "right" }}>{c.submittedValue}</td>
                    <td style={{ textAlign: "right" }} className={c.variance === 0 ? "" : "alert-warn"}>{c.variance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Drawer
        open={openReport != null}
        title={`Rollup integrity · ${openReport ?? ""}`}
        subtitle={openReportDomain ? <span className="soft">Checked {new Date(openReportDomain.checkedAt).toLocaleString()}</span> : undefined}
        onClose={() => setOpenReport(null)}
        width={720}
        footer={<button type="button" className="btn" onClick={() => setOpenReport(null)}>Close</button>}
      >
        {openReportDomain ? (
          integrityRowsSource.length === 0 ? (
            <EmptyState icon="✅" title="All rollups balance" description="Every rollup fact equals the sum of its lineage source values." />
          ) : (
            <div className="table-scroll">
              <TableToolbar
            hideExport
                search={t2.search}
                setSearch={t2.setSearch}
                page={t2.page}
                setPage={t2.setPage}
                pageSize={t2.pageSize}
                setPageSize={t2.setPageSize}
                totalPages={t2.totalPages}
                totalRows={t2.totalRows}
              />
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Rollup fact</th>
                    <th style={{ textAlign: "right" }}>Rollup</th>
                    <th style={{ textAlign: "right" }}>Lineage</th>
                    <th style={{ textAlign: "right" }}>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {(t2.paged as unknown as AggregateRollupIntegrityIssue[]).map((r) => (
                    <tr key={r.rollupFactId}>
                      <td><span className={`pill ${r.status === "mismatch" ? "pill-warn" : "pill-muted"}`}>{r.status}</span></td>
                      <td><code>{r.rollupFactId}</code></td>
                      <td style={{ textAlign: "right" }}>{r.rollupValue}</td>
                      <td style={{ textAlign: "right" }}>{r.lineageSum}</td>
                      <td style={{ textAlign: "right" }}>{r.difference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </Drawer>
    </div>
  );
}
